import type { NotificationRecord } from "@lib/queries/notificationQueries";
import type { ToastType } from "@lib/stores/toast";
import {
  AlertTriangle,
  CheckCircle2,
  Flame,
  Info,
  type LucideIcon,
  OctagonAlert,
  Ticket,
  XCircle,
} from "lucide-react";

/** Rich toast config for a notification event — single source of truth, no scattered switches. */
export interface NotificationToastConfig {
  /** Toast store type to render (success/error/warning/info/blocked…) */
  readonly toastType: ToastType;
  /** Lucide icon override (falls back to the variant's default icon) */
  readonly icon: LucideIcon;
  /** L2.5 outcome token matching the semantics (kept for tooling/tests) */
  readonly token: string;
  /** Label of the "navigate to resource" action (default "Voir") */
  readonly actionLabel?: string;
}

/**
 * Base mapping: hub notification type → rich toast.
 * The backend publishes `eventType` as notification.created|updated|deleted only,
 * so the business semantics resolve from the notification's own `type` axis.
 * The wire NotificationType enum only exposes workflow|alert|info|document|message,
 * so the generic fallbacks below keep a sane default before the topic refinement
 * (TOPIC_REFINEMENTS) narrows the semantics down to success/error/etc.
 * `critical` renders as a blocked toast (a business rule stopped the action) with
 * OctagonAlert — deliberately distinct from a plain error.
 */
const NOTIFICATION_TYPE_TOAST: Readonly<Record<string, NotificationToastConfig>> = {
  info: { toastType: "info", icon: Info, token: "info" },
  success: { toastType: "success", icon: CheckCircle2, token: "success" },
  warning: { toastType: "warning", icon: AlertTriangle, token: "warning" },
  error: { toastType: "error", icon: XCircle, token: "error" },
  critical: { toastType: "blocked", icon: OctagonAlert, token: "blocked" },
  // Wire NotificationType enum values — safe generic fallbacks.
  workflow: { toastType: "info", icon: Info, token: "info" },
  alert: { toastType: "warning", icon: AlertTriangle, token: "warning" },
  document: { toastType: "info", icon: Info, token: "info" },
  message: { toastType: "info", icon: Info, token: "info" },
};

/**
 * Topic refinements (metadata.topic — backend NotificationTopic enum).
 * Keyed by the wire topic string; merged over the base type mapping.
 * Covers EVERY NotificationTopic case so each business event surfaces with
 * its semantic token (success/error/warning/info) and a precise action label.
 */
const TOPIC_REFINEMENTS: Readonly<Record<string, Partial<NotificationToastConfig>>> = {
  // Tickets
  "ticket.cree": { toastType: "success", icon: Ticket, actionLabel: "Voir le ticket" },
  "ticket.resolu": { toastType: "success", icon: Ticket, actionLabel: "Voir le ticket" },
  "ticket.mis_a_jour": { toastType: "info", icon: Ticket, actionLabel: "Voir le ticket" },
  "ticket.assigne": { toastType: "info", icon: Ticket, actionLabel: "Voir le ticket" },
  "ticket.ferme": { toastType: "info", icon: Ticket, actionLabel: "Voir le ticket" },
  "ticket.commente": { toastType: "info", icon: Ticket, actionLabel: "Voir le ticket" },
  // Stock
  "stock.alerte": { toastType: "warning", icon: Flame, actionLabel: "Voir le stock" },
  // RH — Congés
  "rh.conge.demande": { toastType: "info", actionLabel: "Voir le congé" },
  "rh.conge.approuve": { toastType: "success", actionLabel: "Voir le congé" },
  "rh.conge.refuse": { toastType: "error", actionLabel: "Voir le congé" },
  "rh.conge.annule": { toastType: "error", actionLabel: "Voir le congé" },
  // RH — Contrats
  "rh.contrat.valide": { toastType: "success", actionLabel: "Voir le contrat" },
  "rh.contrat.signe": { toastType: "success", actionLabel: "Voir le contrat" },
  "rh.contrat.relance": { toastType: "warning", actionLabel: "Voir le contrat" },
  // RH — Avenants
  "rh.avenant.cree": { toastType: "info", actionLabel: "Voir l'avenant" },
  "rh.avenant.valide": { toastType: "success", actionLabel: "Voir l'avenant" },
  "rh.avenant.signe": { toastType: "success", actionLabel: "Voir l'avenant" },
  // RH — Feuilles de temps
  "rh.feuille_temps.soumise": { toastType: "info", actionLabel: "Voir la feuille de temps" },
  "rh.feuille_temps.validee": { toastType: "success", actionLabel: "Voir la feuille de temps" },
  "rh.feuille_temps.refusee": { toastType: "error", actionLabel: "Voir la feuille de temps" },
  // RH — Notes de frais
  "rh.note_frais.soumise": { toastType: "info", actionLabel: "Voir la note de frais" },
  "rh.note_frais.approuvee": { toastType: "success", actionLabel: "Voir la note de frais" },
  "rh.note_frais.refusee": { toastType: "error", actionLabel: "Voir la note de frais" },
  // RH — Invitations
  "rh.invitation.acquittee": { toastType: "success", actionLabel: "Voir l'invitation" },
  // Achats — Commandes fournisseur
  "achats.commande.creee": { toastType: "info", actionLabel: "Voir la commande" },
  "achats.commande.validee": { toastType: "success", actionLabel: "Voir la commande" },
  "achats.commande.refusee": { toastType: "error", actionLabel: "Voir la commande" },
  "achats.commande.annulee": { toastType: "error", actionLabel: "Voir la commande" },
  "achats.commande.envoyee": { toastType: "success", actionLabel: "Voir la commande" },
  // Achats — Bons de réception
  "achats.bon_reception.livre": { toastType: "success", actionLabel: "Voir la réception" },
  "achats.bon_reception.valide": { toastType: "success", actionLabel: "Voir la réception" },
  // Achats — Factures fournisseur
  "achats.facture.soumise": { toastType: "info", actionLabel: "Voir la facture" },
  "achats.facture.validee": { toastType: "success", actionLabel: "Voir la facture" },
  "achats.facture.refusee": { toastType: "error", actionLabel: "Voir la facture" },
  "achats.facture.payee": { toastType: "success", actionLabel: "Voir la facture" },
  "achats.facture.transferee": { toastType: "success", actionLabel: "Voir la facture" },
  "achats.facture.annulee": { toastType: "error", actionLabel: "Voir la facture" },
  // Ventes — Commandes client
  "ventes.commande.creee": { toastType: "info", actionLabel: "Voir la commande" },
  "ventes.commande.validee": { toastType: "success", actionLabel: "Voir la commande" },
  "ventes.expedition.validee": { toastType: "success", actionLabel: "Voir l'expédition" },
  // Ventes — Factures client
  "ventes.facture.emise": { toastType: "success", actionLabel: "Voir la facture" },
  "ventes.facture.payee": { toastType: "success", actionLabel: "Voir la facture" },
  "ventes.facture.impayee": { toastType: "warning", actionLabel: "Voir la facture" },
  "ventes.facture.transferee": { toastType: "success", actionLabel: "Voir la facture" },
  // Production
  "production.of.lance": { toastType: "info", actionLabel: "Voir l'ordre de fabrication" },
  "production.of.cloture": { toastType: "success", actionLabel: "Voir l'ordre de fabrication" },
  "production.lancement.declenche": { toastType: "info", actionLabel: "Voir le lancement" },
  "production.declaration.validee": { toastType: "success", actionLabel: "Voir la déclaration" },
  // Comptabilité — Relances
  "comptabilite.relance.emise": { toastType: "warning", actionLabel: "Voir la relance" },
  "comptabilite.relance.reglee": { toastType: "success", actionLabel: "Voir la relance" },
  "comptabilite.relance.echue": { toastType: "error", actionLabel: "Voir la relance" },
  // Création — Demandes de travail
  "creation.dt.soumise": { toastType: "info", actionLabel: "Voir la demande de travail" },
  "creation.dt.validee": { toastType: "success", actionLabel: "Voir la demande de travail" },
  "creation.dt.refusee": { toastType: "error", actionLabel: "Voir la demande de travail" },
};

/** Resolve the rich toast config for a notification (type axis + optional topic refinement). */
export const getNotificationToastConfig = (
  notification: NotificationRecord,
): NotificationToastConfig => {
  const base = NOTIFICATION_TYPE_TOAST[notification.type ?? "info"] ?? NOTIFICATION_TYPE_TOAST.info;
  const topic = notification.metadata?.topic;
  const refinement = typeof topic === "string" ? TOPIC_REFINEMENTS[topic] : undefined;
  return refinement ? { ...base, ...refinement } : base;
};

/**
 * Dedupe key for repeated events: the business topic when present (the same
 * event stream morphs ×N, consistent with the +N pill), else type + title so
 * only genuinely identical notifications collapse into one toast.
 */
export const notificationToastDedupeKey = (notification: NotificationRecord): string => {
  const topic = notification.metadata?.topic;
  if (typeof topic === "string" && topic.length > 0) return topic;
  return `notification:${notification.type ?? "info"}:${notification.title ?? ""}`;
};
